import { ClassroomCardSkeleton } from "@/components/skeletons";
import { AppButton } from "@/components/teacher/AppButton";
import { StatPill } from "@/components/teacher/StatPill";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { MyClassroomsDocument } from "@/generated/graphql";
import { format, useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { AssignHomeworkModal } from "./AssignHomeworkModal";

/** Picking the classroom is the whole page: every card opens the assign form for that class. */
export function AssignHomeworkPage() {
  const [assigningClassroomId, setAssigningClassroomId] = useState<string | null>(null);
  const { data, loading, error, refetch } = useQuery(MyClassroomsDocument);
  const t = useT();

  const classrooms = data?.myClassrooms ?? [];

  return (
    <TeacherPage title={t["assign.title"]} subtitle={t["assign.pickClassroom"]}>
      {loading && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {["a", "b", "c", "d"].map((key) => (
            <ClassroomCardSkeleton key={key} />
          ))}
        </div>
      )}

      {error && (
        <div className="app-card space-y-3 p-6 text-center">
          <p className="body text-app-muted">{t["classrooms.loadError"]}</p>
          <AppButton tone="quiet" onClick={() => void refetch()}>
            {t["common.tryAgain"]}
          </AppButton>
        </div>
      )}

      {!(loading || error) && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {classrooms.map((classroom) => (
            <button
              key={classroom.id}
              type="button"
              onClick={() => setAssigningClassroomId(classroom.id)}
              className="app-card app-card-link flex flex-col items-start gap-2 p-5 text-left"
            >
              <span className="app-title w-full truncate">{classroom.name}</span>
              <StatPill>
                {format(t["classrooms.studentCount"], { count: classroom.studentCount })}
              </StatPill>
            </button>
          ))}

          {classrooms.length === 0 && (
            <p className="app-card body-sm p-6 text-app-muted">{t["classrooms.empty"]}</p>
          )}
        </div>
      )}

      {assigningClassroomId && (
        <AssignHomeworkModal
          classroomId={assigningClassroomId}
          onClose={() => setAssigningClassroomId(null)}
        />
      )}
    </TeacherPage>
  );
}
